import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronLeft, ChevronRight, Save, Calendar as CalendarIcon, Check, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { format, eachDayOfInterval, startOfMonth, endOfMonth, addMonths, subMonths, isToday, isFuture, subDays, startOfWeek, endOfWeek, isSameMonth } from 'date-fns';
import { useDailySpend, useSaveDailySpend, CampaignWithSpend } from '@/hooks/useDailySpend';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface Week {
  weekStart: Date;
  weekEnd: Date;
  days: Date[];
  label: string;
}

export function BulkSpendGrid() {
  const { tenantId: companyId } = useTenantContext();
  const { timezone } = useTimezoneUtils();
  const { locale } = useCompanyLocale();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { data: campaigns, isLoading } = useDailySpend(currentMonth, timezone);
  const saveSpend = useSaveDailySpend(locale);

  // Track edited values
  const [editedCells, setEditedCells] = useState<Record<string, string>>({});
  // Track which weeks are expanded (default: current week expanded)
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(() => {
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    return new Set([format(weekStart, 'yyyy-MM-dd')]);
  });

  // Group days by week (Monday-Sunday)
  const weeks = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    // Get the Monday of the week containing the first day of the month
    let weekStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const weeksArray: Week[] = [];

    while (weekStart <= monthEnd) {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const days = eachDayOfInterval({ start: weekStart, end: weekEnd })
        .filter(day => isSameMonth(day, currentMonth));

      if (days.length > 0) {
        weeksArray.push({
          weekStart,
          weekEnd,
          days,
          label: `${format(days[0], 'MMM d')} - ${format(days[days.length - 1], 'MMM d')}`,
        });
      }

      // Move to next week
      weekStart = new Date(weekStart);
      weekStart.setDate(weekStart.getDate() + 7);
    }

    return weeksArray;
  }, [currentMonth]);

  const getCellKey = (campaignId: string, date: string) => `${campaignId}:${date}`;

  const getCellValue = (campaign: CampaignWithSpend, date: string): string => {
    const key = getCellKey(campaign.id, date);
    if (editedCells[key] !== undefined) {
      return editedCells[key];
    }
    const spend = campaign.dailySpend[date];
    return spend ? spend.actual_cost.toString() : '';
  };

  const handleCellChange = (campaignId: string, date: string, value: string) => {
    const key = getCellKey(campaignId, date);
    setEditedCells(prev => ({ ...prev, [key]: value }));
  };

  const hasChanges = Object.keys(editedCells).length > 0;

  const handleSave = () => {
    if (!companyId) return;

    // Map all edited cells - treat empty strings as 0 to allow clearing values
    const entries = Object.entries(editedCells)
      .map(([key, value]) => {
        const [campaignId, date] = key.split(':');
        return {
          campaignId,
          companyId,
          date,
          cost: value === '' ? 0 : (parseFloat(value) || 0),
        };
      });

    if (entries.length === 0) return;

    saveSpend.mutate(entries, {
      onSuccess: () => {
        setEditedCells({});
      }
    });
  };

  const handlePrevMonth = () => setCurrentMonth(prev => subMonths(prev, 1));
  const handleNextMonth = () => setCurrentMonth(prev => addMonths(prev, 1));

  const toggleWeek = (weekKey: string) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(weekKey)) {
        next.delete(weekKey);
      } else {
        next.add(weekKey);
      }
      return next;
    });
  };

  // Calculate week totals for a campaign
  const getWeekTotal = (campaign: CampaignWithSpend, days: Date[]): number => {
    return days.reduce((sum, day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const key = getCellKey(campaign.id, dateStr);
      if (editedCells[key] !== undefined) {
        return sum + (parseFloat(editedCells[key]) || 0);
      }
      return sum + (campaign.dailySpend[dateStr]?.actual_cost || 0);
    }, 0);
  };

  // Calculate stats
  const stats = useMemo(() => {
    if (!campaigns) return { totalDays: 0, filledDays: 0, missingDays: 0 };
    
    const yesterday = subDays(new Date(), 1);
    const allDays = weeks.flatMap(w => w.days);
    const relevantDays = allDays.filter(d => d <= yesterday);
    const totalDays = relevantDays.length * campaigns.length;
    
    let filledDays = 0;
    campaigns.forEach(campaign => {
      relevantDays.forEach(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        if (campaign.dailySpend[dateStr]) {
          filledDays++;
        }
      });
    });

    return { totalDays, filledDays, missingDays: totalDays - filledDays };
  }, [campaigns, weeks]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-96 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="relative">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Daily Spend Grid
              </CardTitle>
              <CardDescription className="mt-1">
                Enter daily ad spend for each campaign. Click a week to expand.
              </CardDescription>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="success">
                  <Check className="h-3 w-3 mr-1" />
                  {stats.filledDays} filled
                </Badge>
                {stats.missingDays > 0 && (
                  <Badge variant="warning">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {stats.missingDays} missing
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={handlePrevMonth}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-medium min-w-[140px] text-center">
                  {format(currentMonth, 'MMMM yyyy')}
                </span>
                <Button variant="outline" size="icon" onClick={handleNextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {weeks.map((week) => {
            const weekKey = format(week.weekStart, 'yyyy-MM-dd');
            const isExpanded = expandedWeeks.has(weekKey);

            return (
              <Collapsible key={weekKey} open={isExpanded} onOpenChange={() => toggleWeek(weekKey)}>
                <div className="border rounded-lg overflow-hidden">
                  {/* Week header */}
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-3 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors">
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="font-medium text-sm">{week.label}</span>
                        <Badge variant="outline" className="text-xs">
                          {week.days.length} days
                        </Badge>
                      </div>
                      {!isExpanded && campaigns && (
                        <div className="text-sm text-muted-foreground">
                          Total: ${campaigns.reduce((sum, c) => sum + getWeekTotal(c, week.days), 0).toFixed(0)}
                        </div>
                      )}
                    </div>
                  </CollapsibleTrigger>

                  {/* Week content */}
                  <CollapsibleContent>
                    <ScrollArea className="w-full whitespace-nowrap">
                      <div className="min-w-max">
                        {/* Header row with dates */}
                        <div className="flex border-b bg-background">
                          <div className="w-[280px] min-w-[280px] p-2 font-medium text-sm border-r">
                            Campaign
                          </div>
                          {week.days.map(day => {
                            const dateStr = format(day, 'yyyy-MM-dd');
                            const dayNum = format(day, 'd');
                            const dayName = format(day, 'EEE');
                            const isWeekend = [0, 6].includes(day.getDay());
                            const isTodayDate = isToday(day);
                            const isFutureDate = isFuture(day);

                            return (
                              <div 
                                key={dateStr}
                                className={cn(
                                  "w-[80px] min-w-[80px] p-1.5 text-center text-xs border-r",
                                  isWeekend && "bg-muted/30",
                                  isTodayDate && "bg-primary/10",
                                  isFutureDate && "opacity-50"
                                )}
                              >
                                <div className="font-medium">{dayName}</div>
                                <div className="text-muted-foreground text-[10px]">{dayNum}</div>
                              </div>
                            );
                          })}
                          <div className="w-[80px] min-w-[80px] p-1.5 text-center text-xs font-medium bg-muted/30">
                            Week Total
                          </div>
                        </div>

                        {/* Campaign rows */}
                        {campaigns?.map(campaign => (
                          <div key={campaign.id} className="flex border-b hover:bg-muted/20">
                            <div className="w-[280px] min-w-[280px] p-2 border-r bg-background">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="text-sm font-medium break-words whitespace-normal">{campaign.name}</div>
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    <p>{campaign.name}</p>
                                    {campaign.lead_source_name && (
                                      <p className="text-xs text-muted-foreground">{campaign.lead_source_name}</p>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {campaign.lead_source_name && (
                                <div className="text-[10px] text-muted-foreground break-words whitespace-normal">
                                  {campaign.lead_source_name}
                                </div>
                              )}
                            </div>
                            {week.days.map(day => {
                              const dateStr = format(day, 'yyyy-MM-dd');
                              const value = getCellValue(campaign, dateStr);
                              const hasData = campaign.dailySpend[dateStr] !== undefined;
                              const isEdited = editedCells[getCellKey(campaign.id, dateStr)] !== undefined;
                              const isWeekend = [0, 6].includes(day.getDay());
                              const isFutureDate = isFuture(day);

                              return (
                                <div 
                                  key={dateStr}
                                  className={cn(
                                    "w-[80px] min-w-[80px] p-0.5 border-r",
                                    isWeekend && "bg-muted/30",
                                    hasData && !isEdited && "bg-success/5",
                                    isEdited && "bg-info/5",
                                    isFutureDate && "opacity-50"
                                  )}
                                >
                                  <Input
                                    type="number"
                                    step="0.01"
                                    placeholder={isFutureDate ? "-" : "0"}
                                    value={value}
                                    onChange={(e) => handleCellChange(campaign.id, dateStr, e.target.value)}
                                    disabled={isFutureDate}
                                    className={cn(
                                      "h-8 text-xs text-center p-1 border-0 bg-transparent focus:bg-background",
                                      hasData && "font-medium",
                                      isEdited && "ring-1 ring-primary"
                                    )}
                                  />
                                </div>
                              );
                            })}
                            <div className="w-[80px] min-w-[80px] p-1.5 text-center text-xs font-medium bg-muted/30 flex items-center justify-center">
                              ${getWeekTotal(campaign, week.days).toFixed(0)}
                            </div>
                          </div>
                        ))}

                        {/* Daily totals row */}
                        <div className="flex border-b bg-muted/50 font-medium">
                          <div className="w-[280px] min-w-[280px] p-2 border-r text-sm">
                            Daily Total
                          </div>
                          {week.days.map(day => {
                            const dateStr = format(day, 'yyyy-MM-dd');
                            const total = campaigns?.reduce((sum, campaign) => {
                              const key = getCellKey(campaign.id, dateStr);
                              if (editedCells[key] !== undefined) {
                                return sum + (parseFloat(editedCells[key]) || 0);
                              }
                              return sum + (campaign.dailySpend[dateStr]?.actual_cost || 0);
                            }, 0) || 0;

                            return (
                              <div 
                                key={dateStr}
                                className="w-[80px] min-w-[80px] p-1.5 text-center text-xs border-r"
                              >
                                {total > 0 ? `$${total.toFixed(0)}` : '-'}
                              </div>
                            );
                          })}
                          <div className="w-[80px] min-w-[80px] p-1.5 text-center text-xs font-bold bg-primary/10">
                            ${campaigns?.reduce((sum, c) => sum + getWeekTotal(c, week.days), 0).toFixed(0) || 0}
                          </div>
                        </div>
                      </div>
                      <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-success/10 border border-success/20" />
              <span>Has data</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-info/10 border border-info/20" />
              <span>Edited</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-muted/30 border" />
              <span>Weekend</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sticky Save Button */}
      <div className="sticky bottom-4 mt-4 flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saveSpend.isPending || !hasChanges}
          size="lg"
          className="gap-2 shadow-lg"
        >
          <Save className="h-5 w-5" />
          {saveSpend.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
